import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AbasSublinhadas, DetailHeader, FaixaKpis, PilulaStatus, type Aba } from "@/components/padroes";
import {
  ESTILO_PARCELA,
  ROTULO_STATUS_PROGRAMA,
  capitalizar,
  estadoParcela,
  parcelaAtual,
  resumirPrograma,
  statusPrograma,
  tituloBonificacao,
} from "@/components/bonificacao/programa";
import { StatusEmissaoPainel } from "@/components/folha/StatusEmissaoPainel";
import { useAuth } from "@/context/AuthContext";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { api } from "@/lib/api";
import { triggerDownload } from "@/lib/download";
import { formatCents, formatDateTime, formatMesRef, parseReaisInput } from "@/lib/format";
import { cn } from "@/lib/utils";
import { formatDataCivil } from "@/data/beneficios";
import type { StatusEmissao } from "@/data/folha";
import {
  descreverCalculo,
  descreverRegras,
  type Bonificacao,
  type BonificacaoItem,
  type CompetenciaBonificacaoDetalhe,
} from "@/data/bonificacao";
import { toast } from "sonner";
import {
  AlertTriangle,
  Download,
  Loader2,
  Lock,
  LockOpen,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Trophy,
} from "lucide-react";

type AbaBonificacao = "recebem" | "nao" | "emissao";

const RHBonificacaoCompetencia = () => {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const nome = useNomeBonificacao();
  const ehRH = user?.role === "rh";

  const [detalhe, setDetalhe] = useState<CompetenciaBonificacaoDetalhe | null>(null);
  const [emissao, setEmissao] = useState<StatusEmissao | null>(null);
  // Totais de cada parcela (o detalhe só traz os da parcela aberta) — vêm da lista.
  const [programa, setPrograma] = useState<Bonificacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [aba, setAba] = useState<AbaBonificacao>("recebem");
  const [acao, setAcao] = useState<"recalcular" | "fechar" | "reabrir" | "excluir" | null>(null);

  const [ajustando, setAjustando] = useState<BonificacaoItem | null>(null);
  const [valorAjuste, setValorAjuste] = useState("");
  const [motivoAjuste, setMotivoAjuste] = useState("");
  const [fecharOpen, setFecharOpen] = useState(false);
  const [observacao, setObservacao] = useState("");
  const [reabrirOpen, setReabrirOpen] = useState(false);
  const [motivoReabertura, setMotivoReabertura] = useState("");
  const [excluirOpen, setExcluirOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = busca.trim() ? `?q=${encodeURIComponent(busca.trim())}` : "";
      const [d, st] = await Promise.all([
        api.get<CompetenciaBonificacaoDetalhe>(`/bonificacoes/competencias/${id}${q}`),
        api.get<StatusEmissao>(`/bonificacoes/competencias/${id}/status-emissao`),
      ]);
      setDetalhe(d);
      setEmissao(st);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar");
    } finally {
      setCarregando(false);
    }
  }, [id, busca]);

  useEffect(() => {
    void load();
  }, [load]);

  // Sem os totais por parcela a tela funciona, só sem o total do programa.
  const bonificacaoId = detalhe?.competencia.bonificacao.id;
  const carregarPrograma = useCallback(() => {
    if (!bonificacaoId) return;
    api
      .get<Bonificacao[]>("/bonificacoes")
      .then((lista) => setPrograma(lista.find((b) => b.id === bonificacaoId) ?? null))
      .catch(() => setPrograma(null));
  }, [bonificacaoId]);

  useEffect(() => {
    carregarPrograma();
  }, [carregarPrograma]);

  /** Depois de fechar, reabrir, ajustar ou recalcular: os totais das parcelas mudam também. */
  const recarregar = async () => {
    await load();
    carregarPrograma();
  };

  // Trocar de parcela volta para a primeira aba (Emissão só existe na fechada).
  useEffect(() => {
    setAba("recebem");
  }, [id]);

  const executar = async (tipo: NonNullable<typeof acao>, fn: () => Promise<unknown>, sucesso: string) => {
    setAcao(tipo);
    try {
      await fn();
      toast.success(sucesso);
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro");
      return false;
    } finally {
      setAcao(null);
    }
  };

  if (carregando || !detalhe) {
    return (
      <RoleGuard role={["rh", "financeiro"]}>
        <AppLayout title={nome}>
          {carregando ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <p className="py-20 text-center text-muted-foreground">Competência não encontrada.</p>
          )}
        </AppLayout>
      </RoleGuard>
    );
  }

  const { competencia: comp, itens, totais, bloqueios } = detalhe;
  const bonificacao = comp.bonificacao;
  const rascunho = comp.status === "rascunho";
  const editavel = ehRH && rascunho;
  const recebem = itens.filter((i) => !i.exclusao);
  const naoRecebem = itens.filter((i) => i.exclusao);
  const todasRascunho = bonificacao.competencias.every((c) => c.status === "rascunho");
  const temAdiantamento = recebem.some((i) => i.adiantamentoDescontado > 0);
  const multiplo = bonificacao.tipoCalculo === "multiplo";

  const parcelas = [...bonificacao.competencias].sort((a, b) => a.parcela - b.parcela);
  const atual = parcelaAtual(parcelas);
  const status = ROTULO_STATUS_PROGRAMA[statusPrograma(parcelas)];
  // Só usa os totais da lista se ela for desta mesma bonificação (troca de parcela).
  const doPrograma = programa?.id === bonificacao.id ? programa : null;
  const totalDaParcela = new Map(doPrograma?.competencias.map((c) => [c.id, c.totalLiquido]) ?? []);
  const resumo = doPrograma ? resumirPrograma(doPrograma.competencias) : null;
  const n = bonificacao.numeroParcelas;

  const exportar = async () => {
    try {
      const { blob, filename } = await api.getFile(`/bonificacoes/competencias/${id}/export.csv`);
      triggerDownload(blob, filename || `${nome} - ${comp.competencia}.csv`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao exportar");
    }
  };

  const abrirAjuste = (i: BonificacaoItem) => {
    setAjustando(i);
    setValorAjuste(i.ajusteManual !== 0 ? String(i.ajusteManual / 100).replace(".", ",") : "");
    setMotivoAjuste(i.ajusteMotivo ?? "");
  };

  const salvarAjuste = async () => {
    if (!ajustando) return;
    let reais: number;
    try {
      reais = valorAjuste.trim() ? parseReaisInput(valorAjuste) : 0;
    } catch {
      return void toast.error("Valor inválido");
    }
    const ok = await executar(
      "recalcular",
      () =>
        api.patch(`/bonificacoes/competencias/${id}/itens/${ajustando.id}`, {
          ajusteManual: reais,
          ajusteMotivo: motivoAjuste.trim() || null,
        }),
      "Ajuste salvo",
    );
    if (ok) {
      setAjustando(null);
      await recarregar();
    }
  };

  const dias = (i: BonificacaoItem) =>
    i.diasApurados === null ? (
      <span className="text-muted-foreground">integral</span>
    ) : (
      <span>
        {i.diasApurados}/{bonificacao.diasPeriodo}{" "}
        <span className="text-muted-foreground">({Math.round((i.diasApurados / bonificacao.diasPeriodo) * 100)}%)</span>
      </span>
    );

  const abas: Aba<AbaBonificacao>[] = [
    { value: "recebem", label: "Recebem", contador: recebem.length },
    { value: "nao", label: "Não recebem", contador: naoRecebem.length },
    ...(!rascunho && emissao
      ? [{ value: "emissao" as const, label: "Emissão", contador: emissao.pendentes > 0 ? emissao.pendentes : null }]
      : []),
  ];
  // Colunas da tabela "Recebem" — o vazio ocupa a linha inteira.
  const colunasRecebem = 8 + (multiplo ? 1 : 0) + (temAdiantamento ? 2 : 0) + (editavel ? 1 : 0);

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title={`${nome} — ${formatMesRef(comp.competencia)}`}>
        <div className="mx-auto flex max-w-[1280px] flex-col gap-4 tabular-nums">
          <DetailHeader
            voltar={{ to: "/rh/bonificacao", label: nome }}
            titulo={tituloBonificacao(bonificacao, nome)}
            status={<PilulaStatus cor={status.cor}>{status.rotulo}</PilulaStatus>}
            acoes={
              ehRH &&
              todasRascunho && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                  disabled={acao !== null}
                  onClick={() => setExcluirOpen(true)}
                >
                  <Trash2 />
                  Excluir
                </Button>
              )
            }
          />

          {/* Regra do programa + parcelas */}
          <section className="flex flex-col gap-[18px] rounded-xl border bg-card px-5 py-5 sm:px-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-primary-text">
                  <Trophy className="h-3.5 w-3.5" />
                  {bonificacao.adiantamento ? "Adiantamento" : "Meta"} {bonificacao.dataBase.slice(0, 4)}
                </span>
                <span className="text-lg font-semibold">
                  {descreverCalculo(bonificacao)} · {n} {n === 1 ? "parcela" : "parcelas"}
                </span>
                <span className="text-[13px] text-muted-foreground">
                  Dias trabalhados contados até {formatDataCivil(bonificacao.dataBase)}
                  {parcelas[0] && ` · 1ª NF em ${formatMesRef(parcelas[0].competencia)}`}
                </span>
                <span className="text-xs text-muted-foreground">Regras: {descreverRegras(bonificacao)}.</span>
              </div>
              {resumo && (
                <div className="flex gap-7">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">Total do programa</span>
                    <span className="text-lg font-semibold">{formatCents(resumo.total)}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">Já pago</span>
                    <span className="text-lg font-semibold">{formatCents(resumo.pago)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              {parcelas.map((c) => {
                const estado = estadoParcela(c, atual);
                const estilo = ESTILO_PARCELA[estado];
                const selecionada = c.id === comp.id;
                const valor = totalDaParcela.get(c.id);
                return (
                  <Link
                    key={c.id}
                    to={`/rh/bonificacao/${c.id}`}
                    aria-current={selecionada ? "page" : undefined}
                    className={cn(
                      "flex flex-col gap-1.5 rounded-[10px] border-[1.5px] px-3.5 py-3 transition-colors",
                      selecionada ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/60",
                    )}
                  >
                    <span className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      Parcela {c.parcela}/{n}
                      <span className={cn("flex items-center gap-1.5 font-semibold", estilo.texto)}>
                        <span className={cn("h-[7px] w-[7px] rounded-full", estilo.ponto)} />
                        {estilo.rotulo}
                      </span>
                    </span>
                    <span className="text-sm font-semibold">{capitalizar(formatMesRef(c.competencia))}</span>
                    <span className="text-xs text-muted-foreground">
                      {valor === undefined ? "—" : `${formatCents(valor)}${estado === "prevista" ? " previsto" : ""}`}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>

          <FaixaKpis
            itens={[
              {
                rotulo: `Parcela ${comp.parcela} · bruto`,
                valor: formatCents(totais.valorBruto),
                apoio: comp.fechadaEm ? `Fechada em ${formatDateTime(comp.fechadaEm)}` : undefined,
              },
              {
                rotulo: "Descontos de empréstimo",
                valor: totais.descontoEmprestimo > 0 ? `− ${formatCents(totais.descontoEmprestimo)}` : formatCents(0),
                tom: totais.descontoEmprestimo > 0 ? "negativo" : undefined,
              },
              { rotulo: "Ajustes", valor: formatCents(totais.ajusteManual) },
              {
                rotulo: "Líquido da parcela",
                valor: formatCents(totais.liquido),
                apoio: `${totais.colaboradores} colaborador${totais.colaboradores === 1 ? "" : "es"}`,
                tom: "destaque",
              },
            ]}
          />

          {rascunho && bloqueios.length > 0 && (
            <div className="flex flex-col gap-2 rounded-[10px] border border-amber-500/35 bg-amber-500/[0.08] px-4 py-3 text-[13px] text-amber-800 dark:text-amber-300">
              {bloqueios.map((b) => (
                <div key={b.codigo} className="flex gap-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p>{b.mensagem}</p>
                    {b.colaboradores.length > 0 && (
                      <p className="text-xs">{b.colaboradores.map((c) => c.nome).join(", ")}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-end justify-between gap-3">
            <AbasSublinhadas value={aba} onChange={setAba} abas={abas} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => void exportar()}>
                <Download />
                Exportar
              </Button>
              {editavel && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={acao !== null}
                    onClick={async () => {
                      if (await executar("recalcular", () => api.post(`/bonificacoes/competencias/${id}/recalcular`, {}), "Recalculada")) await recarregar();
                    }}
                  >
                    {acao === "recalcular" ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                    Recalcular
                  </Button>
                  <Button
                    size="sm"
                    className="bg-foreground text-background hover:bg-foreground/90"
                    onClick={() => setFecharOpen(true)}
                  >
                    <Lock />
                    Fechar parcela {comp.parcela}
                  </Button>
                </>
              )}
              {ehRH && !rascunho && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMotivoReabertura("");
                    setReabrirOpen(true);
                  }}
                >
                  <LockOpen />
                  Reabrir parcela {comp.parcela}
                </Button>
              )}
            </div>
          </div>

          {aba !== "emissao" && (
            <section className="overflow-hidden rounded-xl border bg-card">
              <div className="px-4 py-3.5">
                <div className="relative w-full max-w-[360px]">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por nome ou e-mail"
                    className="pl-9"
                  />
                </div>
              </div>

              {aba === "recebem" ? (
                <Table className="min-w-[1120px] border-t">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Colaborador</TableHead>
                      <TableHead>Admissão</TableHead>
                      <TableHead className="text-right">Salário fixo</TableHead>
                      {multiplo && <TableHead className="text-right">Considerado</TableHead>}
                      <TableHead className="text-right">Dias</TableHead>
                      {temAdiantamento && <TableHead className="text-right">Total</TableHead>}
                      {temAdiantamento && <TableHead className="text-right">Adiantado</TableHead>}
                      <TableHead className="text-right">Parcela</TableHead>
                      <TableHead className="text-right">Empréstimo</TableHead>
                      <TableHead className="text-right">Ajuste</TableHead>
                      <TableHead className="text-right">Líquido</TableHead>
                      {editavel && <TableHead className="w-10" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recebem.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={colunasRecebem} className="py-10 text-center text-muted-foreground">
                          Ninguém recebe nesta parcela.
                        </TableCell>
                      </TableRow>
                    ) : (
                      recebem.map((i) => (
                        <TableRow key={i.id}>
                          <TableCell className="font-medium">
                            <span className="flex flex-wrap items-center gap-1.5">
                              {i.nome}
                              {i.avisos?.includes("SEM_ADMISSAO") && (
                                <span className="rounded-full bg-destructive/15 px-2 py-px text-[10px] font-semibold text-destructive">
                                  sem admissão
                                </span>
                              )}
                              {!i.tomadorNome && (
                                <span className="rounded-full border px-2 py-px text-[10px] font-semibold text-muted-foreground">
                                  sem tomador
                                </span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {i.dataAdmissao ? formatDataCivil(i.dataAdmissao) : "—"}
                          </TableCell>
                          <TableCell className="text-right">{formatCents(i.salarioFixo)}</TableCell>
                          {multiplo && (
                            <TableCell className="text-right">
                              {i.salarioConsiderado !== i.salarioFixo ? (
                                <span title="Limitado ao teto">{formatCents(i.salarioConsiderado)}*</span>
                              ) : (
                                formatCents(i.salarioConsiderado)
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-right">{dias(i)}</TableCell>
                          {temAdiantamento && <TableCell className="text-right">{formatCents(i.valorTotal)}</TableCell>}
                          {temAdiantamento && (
                            <TableCell className="text-right">
                              {i.adiantamentoDescontado > 0 ? `− ${formatCents(i.adiantamentoDescontado)}` : "—"}
                            </TableCell>
                          )}
                          <TableCell className="text-right">{formatCents(i.valorBruto)}</TableCell>
                          <TableCell className={cn("text-right", i.descontoEmprestimo > 0 && "text-destructive")}>
                            {i.descontoEmprestimo > 0 ? `− ${formatCents(i.descontoEmprestimo)}` : "—"}
                          </TableCell>
                          <TableCell className="text-right" title={i.ajusteMotivo ?? undefined}>
                            {i.ajusteManual !== 0 ? formatCents(i.ajusteManual) : "—"}
                          </TableCell>
                          <TableCell className="text-right font-semibold">{formatCents(i.liquido)}</TableCell>
                          {editavel && (
                            <TableCell className="py-1.5 pl-0 pr-3">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground"
                                onClick={() => abrirAjuste(i)}
                                aria-label={`Ajustar ${i.nome}`}
                                title="Ajustar"
                              >
                                <Pencil className="!size-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              ) : (
                <Table className="min-w-[640px] border-t">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Colaborador</TableHead>
                      <TableHead className="w-[130px]">Admissão</TableHead>
                      <TableHead>Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {naoRecebem.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                          Todos os PJ ativos com salário fixo recebem.
                        </TableCell>
                      </TableRow>
                    ) : (
                      naoRecebem.map((i) => (
                        <TableRow key={i.id}>
                          <TableCell className="font-medium">{i.nome}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {i.dataAdmissao ? formatDataCivil(i.dataAdmissao) : "—"}
                          </TableCell>
                          <TableCell>
                            {i.exclusaoRotulo}
                            {i.exclusaoDetalhe && (
                              <span className="block text-xs text-muted-foreground">{i.exclusaoDetalhe}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </section>
          )}
          {aba === "recebem" && multiplo && bonificacao.tetoSalario && (
            <p className="-mt-2 text-xs text-muted-foreground">
              * Salário limitado ao teto de {formatCents(bonificacao.tetoSalario)}.
            </p>
          )}

          {aba === "emissao" && !rascunho && emissao && (
            <StatusEmissaoPainel
              folhaId={id}
              status={emissao}
              podeCobrar={ehRH}
              onAtualizar={() => void recarregar()}
              endpointCobrar={`/bonificacoes/competencias/${id}/cobrar-pendentes`}
              templateEmail="SENDGRID_TEMPLATE_AVISO_BONIFICACAO"
            />
          )}
        </div>
      </AppLayout>

      <Dialog open={!!ajustando} onOpenChange={(v) => !v && setAjustando(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ajustar {ajustando?.nome}</DialogTitle>
            <DialogDescription>
              Soma (ou subtrai, com sinal de menos) do valor desta parcela: {ajustando && formatCents(ajustando.valorBruto)}.
              O desconto de empréstimo é recalculado depois.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ajuste-valor">Ajuste (R$)</Label>
              <Input
                id="ajuste-valor"
                value={valorAjuste}
                onChange={(e) => setValorAjuste(e.target.value)}
                placeholder="Ex.: 500,00 ou -200,00"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ajuste-motivo">Motivo</Label>
              <Input id="ajuste-motivo" value={motivoAjuste} onChange={(e) => setMotivoAjuste(e.target.value)} maxLength={300} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAjustando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => void salvarAjuste()} disabled={acao !== null}>
              {acao === "recalcular" && <Loader2 className="animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fecharOpen} onOpenChange={setFecharOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Fechar parcela {comp.parcela} · {formatMesRef(comp.competencia)}
            </DialogTitle>
            <DialogDescription>
              Libera a NF de bonificação para {totais.colaboradores} colaborador(es), no total de{" "}
              <strong className="text-foreground">{formatCents(totais.liquido)}</strong>, e avisa cada um por e-mail. Os
              descontos de empréstimo viram baixa no saldo de cada um.
            </DialogDescription>
          </DialogHeader>
          {bloqueios.length > 0 ? (
            <div className="space-y-1 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive">Resolva antes de fechar:</p>
              <ul className="list-disc pl-5 text-xs">
                {bloqueios.map((b) => (
                  <li key={b.codigo}>{b.mensagem}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="fechar-obs">Observação (opcional)</Label>
              <Textarea id="fechar-obs" value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} maxLength={500} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFecharOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={bloqueios.length > 0 || acao !== null}
              onClick={async () => {
                const ok = await executar(
                  "fechar",
                  () =>
                    api.post(`/bonificacoes/competencias/${id}/fechar`, observacao.trim() ? { observacao: observacao.trim() } : {}),
                  "Parcela fechada — a NF de bonificação está liberada",
                );
                if (ok) {
                  setFecharOpen(false);
                  await recarregar();
                }
              }}
            >
              {acao === "fechar" && <Loader2 className="animate-spin" />}
              Fechar parcela
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reabrirOpen} onOpenChange={setReabrirOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Reabrir parcela {comp.parcela} · {formatMesRef(comp.competencia)}
            </DialogTitle>
            <DialogDescription>
              As baixas de empréstimo desta parcela são estornadas e a NF de bonificação deixa de estar liberada até o
              novo fechamento.
              {emissao && emissao.emitiram > 0 && ` ${emissao.emitiram} colaborador(es) já emitiram a NF.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reabrir-motivo">Motivo</Label>
            <Textarea
              id="reabrir-motivo"
              value={motivoReabertura}
              onChange={(e) => setMotivoReabertura(e.target.value)}
              rows={2}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReabrirOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={motivoReabertura.trim().length < 5 || acao !== null}
              onClick={async () => {
                const ok = await executar(
                  "reabrir",
                  () => api.post(`/bonificacoes/competencias/${id}/reabrir`, { motivo: motivoReabertura.trim() }),
                  "Parcela reaberta",
                );
                if (ok) {
                  setReabrirOpen(false);
                  await recarregar();
                }
              }}
            >
              {acao === "reabrir" && <Loader2 className="animate-spin" />}
              Reabrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluirOpen} onOpenChange={setExcluirOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta bonificação?</AlertDialogTitle>
            <AlertDialogDescription>
              A bonificação e as {bonificacao.numeroParcelas} parcela(s) serão apagadas. Não é possível desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={acao !== null}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={acao !== null}
              onClick={async (e) => {
                e.preventDefault();
                if (await executar("excluir", () => api.delete(`/bonificacoes/${bonificacao.id}`), "Bonificação excluída")) {
                  setExcluirOpen(false);
                  nav("/rh/bonificacao");
                }
              }}
            >
              {acao === "excluir" && <Loader2 className="animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RoleGuard>
  );
};

export default RHBonificacaoCompetencia;
